import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const ProfileHeader = ({ user, onUpdateProfile }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    location: user?.location || '',
    bio: user?.bio || ''
  });

  // CRITICAL FIX: Update formData when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user?.full_name || '',
        email: user?.email || '',
        location: user?.location || '',
        bio: user?.bio || ''
      });
    }
  }, [user]);

  // CRITICAL FIX: Format join date properly
  const formatJoinDate = (dateString) => {
    if (!dateString) return 'Recently joined';
    
    try {
      const date = new Date(dateString);
      const options = { year: 'numeric', month: 'long' };
      return date?.toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Recently joined';
    }
  };

  // CRITICAL FIX: Handle profile image upload
  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !user?.id) return;

    setIsUploadingImage(true);
    
    try {
      // Delete old avatar if it exists and is from our storage
      if (user?.avatar_url && user?.avatar_url?.includes('profile-images')) {
        const oldPath = user?.avatar_url?.split('/')?.slice(-2)?.join('/'); // Get the last two parts (folder/filename)
        try {
          await supabase?.storage?.from('profile-images')?.remove([oldPath]);
        } catch (deleteError) {
          console.warn('Could not delete old avatar:', deleteError);
          // Continue with upload even if delete fails
        }
      }

      // Upload new image
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { error: uploadError } = await supabase?.storage
        ?.from('profile-images')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL (since bucket is public)
      const { data: urlData } = supabase?.storage
        ?.from('profile-images')
        ?.getPublicUrl(filePath);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to get public URL for uploaded image');
      }

      // Update user profile in database
      const { error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({ avatar_url: urlData?.publicUrl })
        ?.eq('id', user?.id);

      if (updateError) throw updateError;

      // Notify parent component - this will trigger AuthContext refresh
      onUpdateProfile?.({ ...user, avatar_url: urlData?.publicUrl });

      console.log('Profile image uploaded successfully:', urlData?.publicUrl);

    } catch (error) {
      console.error('Error uploading image:', error);
      let errorMessage = 'Failed to upload image. Please try again.';
      
      // Provide more specific error messages
      if (error?.message?.includes('storage')) {
        errorMessage = 'Storage error. Please check your connection and try again.';
      } else if (error?.message?.includes('size')) {
        errorMessage = 'Image file is too large. Please choose a smaller image (max 5MB).';
      } else if (error?.message?.includes('type')) {
        errorMessage = 'Invalid file type. Please use JPG, PNG, or WebP images.';
      }
      
      alert(errorMessage);
    } finally {
      setIsUploadingImage(false);
      // Reset file input
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      // Update user profile in database
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({
          full_name: formData?.full_name,
          location: formData?.location,
          bio: formData?.bio
        })
        ?.eq('id', user?.id);

      if (error) throw error;

      // Notify parent component
      onUpdateProfile?.({ ...user, ...formData });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: user?.full_name || '',
      email: user?.email || '',
      location: user?.location || '',
      bio: user?.bio || ''
    });
    setIsEditing(false);
  };

  // CRITICAL FIX: Handle camera button click
  const handleCameraClick = () => {
    if (isUploadingImage) return;
    fileInputRef?.current?.click();
  };

  return (
    <div className="bg-card rounded-lg p-6 shadow-organic">
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        {/* Profile Picture */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted">
              <Image
                src={user?.avatar_url}
                alt={`${user?.full_name || 'User'}'s profile`}
                className="w-full h-full object-cover"
              />
            </div>
            <button
              onClick={handleCameraClick}
              disabled={isUploadingImage}
              className={`absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center shadow-organic transition-gentle ${
                isUploadingImage 
                  ? 'opacity-50 cursor-not-allowed' :'hover:bg-primary/90'
              }`}
            >
              {isUploadingImage ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="Camera" size={16} color="white" />
              )}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            {isUploadingImage ? 'Uploading...' : 'Click to update photo'}
          </p>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/jpg"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>

        {/* Profile Information */}
        <div className="flex-1">
          {isEditing ? (
            <div className="space-y-4">
              <Input
                label="Full Name"
                name="full_name"
                value={formData?.full_name}
                onChange={handleInputChange}
                required
              />
              
              <Input
                label="Email Address"
                name="email"
                type="email"
                value={formData?.email}
                disabled
                className="bg-muted cursor-not-allowed"
              />
              
              <Input
                label="Location"
                name="location"
                value={formData?.location}
                onChange={handleInputChange}
                placeholder="City, UK"
              />
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Bio
                </label>
                <textarea
                  name="bio"
                  value={formData?.bio}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-gentle resize-none"
                  placeholder="Tell us about your wellness journey..."
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  iconName="Check"
                  iconPosition="left"
                >
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  iconName="X"
                  iconPosition="left"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  {/* CRITICAL FIX: Use full_name instead of name */}
                  <h1 className="text-2xl font-semibold text-foreground mb-1">
                    {user?.full_name || 'User'}
                  </h1>
                  {user?.location && (
                    <div className="flex items-center text-muted-foreground text-sm mb-2">
                      <Icon name="MapPin" size={16} className="mr-1" />
                      {user?.location}
                    </div>
                  )}
                  <div className="flex items-center text-muted-foreground text-sm">
                    <Icon name="Mail" size={16} className="mr-1" />
                    {user?.email}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  iconName="Edit2"
                  iconPosition="left"
                >
                  Edit Profile
                </Button>
              </div>
              
              {user?.bio && (
                <p className="text-foreground text-sm leading-relaxed mb-4">
                  {user?.bio}
                </p>
              )}
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                {/* CRITICAL FIX: Use created_at for member since date */}
                <span>Member since {formatJoinDate(user?.created_at)}</span>
                <span>•</span>
                <span>{user?.savedCount || 0} saved items</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;